/**
 * Project API Service
 * Frontend service for project management endpoints
 */

import { useAuth } from '../hooks/useAuth';

// Reuse the same base URL and auth helper pattern as teamApi
const RAW_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';
const API_BASE = RAW_API_BASE.replace(/\/$/, '').endsWith('/api')
  ? RAW_API_BASE.replace(/\/$/, '')
  : `${RAW_API_BASE.replace(/\/$/, '')}/api`;

function getAuthToken(): string | null {
  const authData = localStorage.getItem('cd-people-auth');
  if (authData) {
    try {
      const { state } = JSON.parse(authData);
      return state.token || null;
    } catch {
      return null;
    }
  }
  return null;
}

function getHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function handle401(response: Response) {
  if (response.status === 401) {
    useAuth.getState().logout();
  }
}

// ============================================================================
// Types
// ============================================================================

export interface Project {
  id: number;
  name: string;
  objective?: string;
  description?: string;
  project_type?: string;
  domain?: string;
  status: string;
  default_location?: string;
  start_date?: string;
  end_date?: string;
  tags?: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectWithTeams extends Project {
  teams: any[];
  team_count: number;
}

export interface CreateProjectData {
  name: string;
  objective?: string;
  description?: string;
  project_type?: string;
  domain?: string;
  status?: string;
  default_location?: string;
  start_date?: string;
  end_date?: string;
  tags?: string[];
}

export interface ProjectUser {
  user_email: string;
  role: 'owner' | 'editor' | 'viewer';
}

// ============================================================================
// CRUD
// ============================================================================

export interface ProjectsResponse {
  projects: ProjectWithTeams[];
  unassigned_teams: any[];
}

export async function listProjects(): Promise<ProjectsResponse> {
  const response = await fetch(`${API_BASE}/projects`, {
    method: 'GET',
    headers: getHeaders(),
  });
  handle401(response);
  if (!response.ok) throw new Error(`Failed to list projects: ${response.statusText}`);
  return await response.json();
}

export async function getProject(projectId: number): Promise<ProjectWithTeams> {
  const response = await fetch(`${API_BASE}/projects/${projectId}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  handle401(response);
  if (!response.ok) throw new Error(`Failed to get project: ${response.statusText}`);
  return await response.json();
}

export async function createProject(data: CreateProjectData): Promise<Project> {
  const response = await fetch(`${API_BASE}/projects`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  handle401(response);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to create project: ${response.statusText}`);
  }
  return await response.json();
}

export async function updateProject(projectId: number, data: Partial<CreateProjectData>): Promise<Project> {
  const response = await fetch(`${API_BASE}/projects/${projectId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  handle401(response);
  if (!response.ok) throw new Error(`Failed to update project: ${response.statusText}`);
  return await response.json();
}

export async function deleteProject(projectId: number): Promise<void> {
  const response = await fetch(`${API_BASE}/projects/${projectId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  handle401(response);
  if (!response.ok) throw new Error(`Failed to delete project: ${response.statusText}`);
}

// ============================================================================
// Sharing
// ============================================================================

export async function shareProject(projectId: number, email: string, role: string): Promise<void> {
  const response = await fetch(`${API_BASE}/projects/${projectId}/share`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ email, role }),
  });
  handle401(response);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to share project: ${response.statusText}`);
  }
}

export async function revokeProjectAccess(projectId: number, email: string): Promise<void> {
  const response = await fetch(`${API_BASE}/projects/${projectId}/share/${encodeURIComponent(email)}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  handle401(response);
  if (!response.ok) throw new Error(`Failed to revoke access: ${response.statusText}`);
}

export async function getProjectUsers(projectId: number): Promise<ProjectUser[]> {
  const response = await fetch(`${API_BASE}/projects/${projectId}/users`, {
    method: 'GET',
    headers: getHeaders(),
  });
  handle401(response);
  if (!response.ok) throw new Error(`Failed to get project users: ${response.statusText}`);
  return await response.json();
}

// ============================================================================
// Quality
// ============================================================================

export interface QualityCheckResult {
  summary: string;
  score: number;
  status: 'excellent' | 'good' | 'needs_attention' | 'incomplete';
  issues: Array<{
    id: string;
    severity: 'low' | 'medium' | 'high';
    field: string;
    message: string;
    suggestion: string;
  }>;
  suggestions: string[];
  clarifying_questions: string[];
}

export async function getTeamQuality(teamId: number): Promise<QualityCheckResult> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/quality`, {
    method: 'GET',
    headers: getHeaders(),
  });
  handle401(response);
  if (!response.ok) throw new Error(`Failed to get quality: ${response.statusText}`);
  return await response.json();
}

// ============================================================================
// Team Sharing (via teams endpoint)
// ============================================================================

export async function shareTeam(teamId: number, email: string, role: string): Promise<void> {
  const response = await fetch(`${API_BASE}/teams/${teamId}/share`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ email, role }),
  });
  handle401(response);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to share team: ${response.statusText}`);
  }
}

// ============================================================================
// Chat Session Persistence
// ============================================================================

export interface ChatSession {
  id: string;
  team_id?: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: number;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  user_email?: string;
  candidates_json?: string;
  candidate_context_json?: string;
  created_at: string;
}

export async function getChatSessionForTeam(teamId: number): Promise<ChatSession | null> {
  const response = await fetch(`${API_BASE}/chat/sessions/${teamId}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  handle401(response);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Failed to get chat session: ${response.statusText}`);
  return await response.json();
}

export async function getChatHistory(sessionId: string, limit = 100, offset = 0): Promise<ChatMessage[]> {
  const response = await fetch(`${API_BASE}/chat/history/${sessionId}?limit=${limit}&offset=${offset}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  handle401(response);
  if (!response.ok) throw new Error(`Failed to get chat history: ${response.statusText}`);
  const data = await response.json();
  return data.messages || [];
}
